import type { Instrument } from '@nutrimatic/sdk'
import {
  NATIVE_LEG,
  NATIVE_TOKEN_ADDRESS,
  toCatalogAddress,
  type TokenCatalogEntry,
} from '@sirius-cybernetics-42/product-config'

import { getNativeCurrency } from '@/config/native'
import { ChainId } from '@/sdk'
import { isWrappedNativeAddress } from '@/data/marketApi/normalizeMarketToken'

/** Clave de catálogo para un instrumento Nutrimatic (nativo sin address → 0x0). */
export function instrumentCatalogKey(instrument: Instrument): string {
  if (!instrument.onChainAddress) {
    return NATIVE_TOKEN_ADDRESS.toLowerCase()
  }
  return toCatalogAddress(instrument.onChainAddress)
}

function entryCatalogKey(entry: TokenCatalogEntry): string {
  return toCatalogAddress(entry.onChainAddress ?? entry.catalogAddress)
}

/** Address presente en el listado Nutrimatic (instrumentos del hub). */
export function isNutrimaticListedAddress(
  address: string,
  instruments: readonly Instrument[] | undefined,
): boolean {
  if (!instruments?.length) return false
  const lower = toCatalogAddress(address)
  return instruments.some((instrument) => {
    if (!instrument.onChainAddress) {
      return lower === NATIVE_TOKEN_ADDRESS.toLowerCase()
    }
    return toCatalogAddress(instrument.onChainAddress) === lower
  })
}

/**
 * Convierte un instrumento Nutrimatic en entrada de catálogo operativo.
 * Solo-Nutrimatic: `pickerPlacement: 'list'`; nativo sin address → `role: 'native'`.
 */
export function instrumentToCatalogEntry(
  chainId: ChainId,
  instrument: Instrument,
): TokenCatalogEntry {
  const isNative = !instrument.onChainAddress
  const native = getNativeCurrency(chainId)
  const decimals = instrument.decimals ?? (isNative ? native.decimals : 18)
  const symbol = instrument.symbol || (isNative ? native.symbol ?? 'NATIVE' : 'TOKEN')
  const name = instrument.displayName || instrument.symbol || (isNative ? native.name ?? symbol : symbol)

  if (isNative) {
    return {
      catalogAddress: NATIVE_TOKEN_ADDRESS.toLowerCase(),
      onChainAddress: NATIVE_LEG.wrappedAddress,
      symbol,
      name,
      decimals,
      role: 'native',
      pickerPlacement: 'list',
      swapEnabled: true,
      addLiquidityEnabled: true,
      removeLiquidityEnabled: true,
    }
  }

  const address = instrument.onChainAddress!.toLowerCase()
  return {
    catalogAddress: address,
    onChainAddress: address,
    symbol,
    name,
    decimals,
    role: 'erc20',
    pickerPlacement: 'list',
    swapEnabled: true,
    addLiquidityEnabled: true,
    removeLiquidityEnabled: true,
  }
}

/** Identidad Nutrimatic + flags de política / placement de product-config. */
function mergeCollision(
  productEntry: TokenCatalogEntry,
  nutrimaticEntry: TokenCatalogEntry,
): TokenCatalogEntry {
  return {
    ...productEntry,
    symbol: nutrimaticEntry.symbol,
    name: nutrimaticEntry.name,
    decimals: nutrimaticEntry.decimals,
    onChainAddress: nutrimaticEntry.onChainAddress ?? productEntry.onChainAddress,
    catalogAddress:
      productEntry.role === 'native'
        ? productEntry.catalogAddress
        : nutrimaticEntry.catalogAddress || productEntry.catalogAddress,
    // Listado Nutrimatic = oficial y visible; STARSHIP u otros hidden de PC siguen ocultos si no vienen del hub.
    pickerPlacement:
      productEntry.pickerPlacement === 'hidden' ? 'list' : productEntry.pickerPlacement,
  }
}

/**
 * Fusiona catálogo product-config + instrumentos Nutrimatic.
 * Nutrimatic gana identidad en colisión; PC conserva routing/policy.
 * Un token listado en Nutrimatic deja de ser `hidden` (oficial).
 * Sin instrumentos → copia de product-config (fallback).
 */
export function mergeTokenCatalogEntries(
  productEntries: readonly TokenCatalogEntry[],
  instruments: readonly Instrument[],
  chainId: ChainId,
): TokenCatalogEntry[] {
  if (instruments.length === 0) {
    return productEntries.map((entry) => ({ ...entry }))
  }

  const byKey = new Map<string, TokenCatalogEntry>()
  for (const entry of productEntries) {
    byKey.set(entryCatalogKey(entry), { ...entry })
  }

  const hasNativeInstrument = instruments.some((i) => !i.onChainAddress)

  for (const instrument of instruments) {
    // Misma pierna que el nativo: el instrumento sin address (o PC) ya la cubre.
    if (
      instrument.onChainAddress &&
      isWrappedNativeAddress(instrument.onChainAddress, chainId) &&
      (hasNativeInstrument || byKey.has(NATIVE_TOKEN_ADDRESS.toLowerCase()))
    ) {
      continue
    }

    const key = instrumentCatalogKey(instrument)
    const fromNutrimatic = instrumentToCatalogEntry(chainId, instrument)
    const existing = byKey.get(key)
    if (existing) {
      byKey.set(key, mergeCollision(existing, fromNutrimatic))
    } else {
      byKey.set(key, fromNutrimatic)
    }
  }

  return [...byKey.values()]
}
